/* eslint-disable @typescript-eslint/no-use-before-define */

import { LiquiditySummaryDataState } from './store/data/LiquiditySummaryDataState';
import { OrderSide } from 'communication/types/order/OrderSide';
import { getSideClass } from 'utils/css/cssUtils';

export interface LiquiditySummaryItem {
    shares?: number;
    value?: number;
    orderLeaves?: number;
    fullOrder?: number;
    propOfAdv?: number;
    totalVsHist?: number;
}

export interface LiquiditySummaryData extends LiquiditySummaryDataState {
    stats: {
        predictedRemaining: LiquiditySummaryItem;
        actualVolume: LiquiditySummaryItem;
        adv: LiquiditySummaryItem;
        historicalOpen: LiquiditySummaryItem;
        historicalClose: LiquiditySummaryItem;
        predictedClose: LiquiditySummaryItem;
    };
    orderQty: number;
    orderLeaves: number | undefined;
    sideClass: string;
}

export const computePredictedRemainingVolume = (
    data: LiquiditySummaryDataState | undefined
) =>
    data?.rtaPredRemVol === 0
        ? undefined // after the close
        : (data?.rtaPredRemVol ?? 0) -
          (data?.predictedClosingAuctionVolume ?? 0) +
          (data?.predCloseVol ?? 0);

export function getLiquiditySummaryData(
    data: LiquiditySummaryDataState | undefined,
    exchangeRate: number,
    orderQty: number,
    orderLeaves: number | undefined,
    side: OrderSide | undefined
): LiquiditySummaryData | undefined {
    if (!data) {
        return undefined;
    }
    return {
        ...data,
        orderQty: Math.abs(orderQty),
        orderLeaves:
            orderLeaves !== undefined ? Math.abs(orderLeaves) : undefined,
        sideClass: getSideClass(side || 'B'),
        stats: {
            predictedRemaining: new PredictedRemainingItemBuilder(
                data,
                exchangeRate,
                orderQty,
                orderLeaves
            ).item,
            actualVolume: new ActualVolumeItemBuilder(
                data,
                exchangeRate,
                orderQty,
                orderLeaves
            ).item,
            adv: new AdvItemBuilder(data, exchangeRate, orderQty, orderLeaves)
                .item,
            historicalOpen: new HistoricalOpenItemBuilder(
                data,
                exchangeRate,
                orderQty,
                orderLeaves
            ).item,
            historicalClose: new HistoricalCloseItemBuilder(
                data,
                exchangeRate,
                orderQty,
                orderLeaves
            ).item,
            predictedClose: new PredictedCloseItemBuilder(
                data,
                exchangeRate,
                orderQty,
                orderLeaves
            ).item,
        },
    };
}

abstract class ItemBuilderBase {
    public item: LiquiditySummaryItem;
    public constructor(
        protected data: LiquiditySummaryDataState,
        exchangeRate: number,
        private orderQty: number,
        private orderLeaves: number | undefined,
        shares: number | undefined
    ) {
        this.item = { shares };
        if (shares !== undefined) {
            this.calculateValue(exchangeRate);
            this.calculateOrderLeaves();
            this.calculateFullOrder();
            this.calculateAdv();
            this.calculateTodayVsHist();
        }
    }

    protected calculateValue(exchangeRate: number): void {
        if (
            this.item.shares === undefined ||
            this.data.previousClosePrice === undefined
        ) {
            return;
        }
        this.item.value =
            this.item.shares * this.data.previousClosePrice * exchangeRate;
    }

    protected calculateOrderLeaves() {
        if (this.item.shares === 0 || this.orderLeaves === undefined) {
            return;
        }

        this.item.orderLeaves = (this.orderLeaves / this.item.shares!) * 100;
    }

    protected calculateFullOrder() {
        if (this.item.shares === 0) {
            return;
        }

        this.item.fullOrder = (this.orderQty / this.item.shares!) * 100;
    }

    protected calculateAdv() {
        if (!this.data.averageDailyVolume) {
            return;
        }

        this.item.propOfAdv =
            (this.item.shares! / this.data.averageDailyVolume) * 100;
    }

    protected calculateTodayVsHist() {
        return;
    }
}

class PredictedRemainingItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        orderLeaves: number | undefined
    ) {
        super(
            data,
            exchangeRate,
            orderQty,
            orderLeaves,
            computePredictedRemainingVolume(data)
        );
    }

    protected calculateFullOrder() {
        return;
    }

    protected calculateAdv() {
        return;
    }

    protected calculateTodayVsHist() {
        this.item.totalVsHist = this.data.actualVsHistPct;
    }
}

class ActualVolumeItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        orderLeaves: number | undefined
    ) {
        super(data, exchangeRate, orderQty, orderLeaves, data.volume);
    }

    protected calculateOrderLeaves() {
        return;
    }

    protected calculateFullOrder() {
        return;
    }

    protected calculateAdv() {
        return;
    }

    protected calculateTodayVsHist() {
        if (
            this.item.shares === undefined ||
            this.data.predictedTradedVolume === undefined
        ) {
            return;
        }

        const top = this.item.shares - this.data.predictedTradedVolume;
        const bottom = this.data.predictedTradedVolume ?? 0;
        if (bottom !== 0) {
            this.item.totalVsHist = (top / bottom) * 100;
        }
    }
}

class AdvItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        orderLeaves: number | undefined
    ) {
        super(
            data,
            exchangeRate,
            orderQty,
            orderLeaves,
            data.averageDailyVolume
        );
    }
}

class HistoricalOpenItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        orderLeaves: number | undefined
    ) {
        super(data, exchangeRate, orderQty, orderLeaves, data.histOpenVol);
    }
}

class HistoricalCloseItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        orderLeaves: number | undefined
    ) {
        super(data, exchangeRate, orderQty, orderLeaves, data.histCloseVol);
    }
}

class PredictedCloseItemBuilder extends ItemBuilderBase {
    public constructor(
        data: LiquiditySummaryDataState,
        exchangeRate: number,
        orderQty: number,
        _orderLeaves: number | undefined
    ) {
        super(data, exchangeRate, orderQty, undefined, data.predCloseVol);
    }

    protected calculateOrderLeaves() {
        return;
    }

    protected calculateFullOrder() {
        return;
    }

    protected calculateAdv() {
        return;
    }

    protected calculateTodayVsHist() {
        this.item.totalVsHist = this.data.actualVsHistPct;
    }
}
